import React, { useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DirectorySearch } from '../components/DirectorySearch/DirectorySearch';
import { theme } from 'antd';
import { ShowListing } from '../components/DirectoryListings/ShowListing/ShowListing';
import { useShows } from '../api/queries';
import { spacing } from '../theme/tokens/spacing';
import { DateSubheader } from '../components/DateSubheader';
import { useHeader } from '../components/Layout/MainLayout';
import type { Show } from '../api';

function Shows() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { data: shows, isLoading, error } = useShows();
  const { searchQuery } = useHeader();
  const q = searchQuery.toLowerCase();

  const handleShowClick = (showId: string) => {
    navigate(`/shows/${showId}`);
  };

  // Format date to "Day Month Date" format (e.g., "Wed Sep 17")
  const formatDate = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get date label for grouping (Today, Tomorrow, or the formatted date)
  const getDateLabel = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const showDate = new Date(year, month - 1, day);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    // Reset time parts for comparison
    showDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);

    if (showDate.getTime() === today.getTime()) {
      return { label: formatDate(dateString), pill: 'Today', pillColor: 'red' as const };
    } else if (showDate.getTime() === tomorrow.getTime()) {
      return { label: formatDate(dateString), pill: 'Tomorrow', pillColor: 'grey' as const };
    } else {
      return { label: formatDate(dateString), pill: undefined, pillColor: undefined };
    }
  };

  // Ref for today's section to scroll to on mount
  const todaySectionRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // Get today's date key for comparison
  const todayKey = useMemo(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }, []);

  // Find the target date to scroll to (today, or nearest past date if no shows today)
  const getScrollTargetDate = (groups: [string, Show[]][]) => {
    const todayTime = new Date(todayKey).getTime();

    // First check if today exists
    if (groups.some(([date]) => date === todayKey)) {
      return todayKey;
    }

    // Find the nearest past date (first date that's before today in our descending list)
    // Since list is sorted descending, we look for the first date <= today
    for (const [date] of groups) {
      if (new Date(date).getTime() <= todayTime) {
        return date;
      }
    }

    // If no past dates, return the last item (oldest future date, closest to today)
    return groups.length > 0 ? groups[groups.length - 1][0] : null;
  };

  // Group shows by date
  const groupShowsByDate = (showsList: Show[]) => {
    const groups: { [key: string]: Show[] } = {};

    showsList.forEach(show => {
      const dateKey = show.date.split('T')[0]; // Get just the date part
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(show);
    });

    // Sort dates descending (future at top, past at bottom)
    // This way: scroll up = future, scroll down = past
    return Object.entries(groups).sort(([a], [b]) =>
      new Date(b).getTime() - new Date(a).getTime()
    );
  };

  // Filter shows based on search query
  const filteredShows = shows
    ? q
      ? shows.filter(show =>
          (show.band_name || '').toLowerCase().includes(q) ||
          (show.venue_name || '').toLowerCase().includes(q) ||
          (show.address || '').toLowerCase().includes(q) ||
          (show.description || '').toLowerCase().includes(q)
        )
      : shows
    : [];

  const groupedShows = groupShowsByDate(filteredShows);

  // Scroll to top first, then to today's section on mount
  useEffect(() => {
    // Always scroll to top on mount to prevent partial scroll appearance
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!hasScrolledRef.current && todaySectionRef.current && groupedShows.length > 0) {
      // Small delay to ensure layout is complete
      requestAnimationFrame(() => {
        todaySectionRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
        hasScrolledRef.current = true;
      });
    }
  }, [groupedShows.length]);

  return (
    <div className="directory-page">
      <div className="directory-page__tabs">
        <DirectorySearch activeTab="shows" />
      </div>
      <div className="body-temp directory-page__list">
        {isLoading && (
          <div style={{ fontSize: token.fontSizeSM, lineHeight: token.lineHeightSM, color: token.colorTextSecondary }}>
            Loading shows...
          </div>
        )}

        {error && (
          <div style={{ fontSize: token.fontSizeSM, lineHeight: token.lineHeightSM, color: token.colorError }}>
            Error loading shows: {error.message}
          </div>
        )}

        {!isLoading && !error && groupedShows.length === 0 && (
          <div style={{ fontSize: token.fontSizeSM, lineHeight: token.lineHeightSM, color: token.colorTextSecondary }}>
            No shows found.
          </div>
        )}

        {(() => {
          const scrollTargetDate = getScrollTargetDate(groupedShows);
          return groupedShows.map(([date, dateShows]) => {
            const { label, pill, pillColor } = getDateLabel(date);
            const isScrollTarget = date === scrollTargetDate;

            return (
              <div
                key={date}
                ref={isScrollTarget ? todaySectionRef : undefined}
                style={{ marginBottom: spacing.px24 }}
              >
                <DateSubheader
                dateLabel={label}
                pillLabel={pill}
                pillColor={pillColor}
                showNavArrows={false}
                dateClickable={false}
              />

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.px16,
                marginTop: spacing.px16,
              }}>
                {dateShows.map((show) => (
                  <ShowListing
                    key={show.id}
                    bandName={show.band_name || 'Unknown Band'}
                    imageUrl={show.band_photo_url}
                    soundcloudUrl={show.band_soundcloud_url}
                    date={formatDate(show.date)}
                    description={show.description}
                    venue={show.venue_name || show.address || 'Unknown Venue'}
                    time={show.time}
                    onClick={() => handleShowClick(show.id)}
                  />
                ))}
              </div>
            </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

export default Shows;
